/* eslint-disable @typescript-eslint/no-explicit-any */
import { DashOutlined } from '@ant-design/icons'
import { SorterResult } from 'antd/es/table/interface'
import { IDegree } from 'app/api/degree/model'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { IPayLoadGetList, ISort } from 'app/common/interface'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce } from 'app/hook'
import { useGetDegreeList } from 'app/react-query/hook/degree'
import { handleDownloadExcel } from 'app/service/general/service'
import { Pagination, initialPagination } from 'parkway-web-common'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UpdateModalLayout } from './layouts'
import { DeleteModalLayout } from './layouts/DeleteModal'

export const useHook = () => {
  const { user, verifyPermission } = useVerifyPermission()
  const { t } = useTranslation()
  const [filter, setFilter] = useState<IPayLoadGetList>({})

  const filterDebounce = useDebounce(filter, 500)

  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [sorts, setSort] = useState<ISort[]>([])

  const { data, isLoading } = useGetDegreeList({
    limit: pagination.pageSize,
    page: pagination?.current,
    search: filter?.search,
    sorts,
  })

  const flattenData = useMemo(() => {
    return data
  }, [data])

  const dataTable = useMemo(() => {
    return flattenData?.data
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? dataTable?.length
  }, [flattenData])

  const isHavePermissionUpdateDegree = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateDegree)
  }, [user])

  const isHavePermissionDeleteDegree = useMemo(() => {
    return verifyPermission(PermissionEnum.DeleteDegree)
  }, [user])

  const columns = useMemo(() => {
    return [
      convertColumnTable<IDegree>({
        key: 'name',
        title: 'Tên',
        classNameWidthColumnOverwrite: 'normal-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText children={record?.name ?? '-'} fontWeight="semibold" />
          )
        },
      }),

      convertColumnTable<IDegree>({
        key: 'code',
        title: 'Mã',
        classNameWidthColumnOverwrite: 'medium-column',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText children={record?.code ?? '-'} fontWeight="semibold" />
          )
        },
      }),

      convertColumnTable<IDegree>({
        key: 'description',
        title: 'Mô tả',
        classNameWidthColumnOverwrite: 'big-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              children={record?.description ?? '-'}
              fontWeight="semibold"
            />
          )
        },
      }),

      convertColumnTable<IDegree>({
        key: 'id',
        title: '',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => {
          return (
            <BasePopover
              trigger="click"
              content={
                <BaseSpace>
                  {isHavePermissionUpdateDegree ? (
                    <UpdateModalLayout degree={record} />
                  ) : null}

                  {isHavePermissionDeleteDegree ? (
                    <DeleteModalLayout name={record?.name} id={record?.id} />
                  ) : null}
                </BaseSpace>
              }
            >
              <DashOutlined rev={undefined} rotate={90} />
            </BasePopover>
          )
        },
      }),
    ]
  }, [t])

  const handleTableChange = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const onChangeFilter = (newFilter: IPayLoadGetList) => {
    setFilter(newFilter)
    setPagination(initialPagination)
  }
  const downloadExcel = async (): Promise<void> => {
    await handleDownloadExcel({
      params: {
        search: filterDebounce?.search,
      },
      urlMethod:  `${ENV_CONFIG.API_ENDPOINT}admin/degree/download`,
      fileName: 'Công đoàn bộ phận'
    })
    
  }
  return {
    isLoading,
    columns,
    pagination: {
      ...pagination,
      total,
    },
    dataTable,
    handleTableChange,
    onChangeFilter,
    filter,
    downloadExcel,
  }
}
